import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService } from '../../services/navigation/navigation.service';
import { CacheService } from '../../services/cache/cache.service';
import { CommonService } from '../../services/common/common.service';
import { Column } from '../interfaces/column.interface';
import moment from 'moment';
import { BannerTypesDataService } from './banner-types-data.service';
import { BannerType, BannerTypesResult } from '../interfaces';
import { UsersDataService } from './users-data.service';
import { User, UsersResult } from '../interfaces';
import { BannerPositionsDataService } from './banner-positions-data.service';
import { BannerPosition, BannerPositionsResult } from '../interfaces';

@Injectable({
  providedIn: 'root'
})
export class BannersColumnsService {

  public bannerTypes: BannerType[] = [];
  public users: User[] = [];
  public bannerPositions: BannerPosition[] = [];
  public bannerPositionsFiltered: BannerPosition[] = [];
  public gendersOptions = [{
    value: 'All',
    label: 'GENDERSALL',
    css: 'primary'
  }, {
    value: 'Male',
    label: 'GENDERSMALE',
    css: 'success'
  }, {
    value: 'Female',
    label: 'GENDERSFEMALE',
    css: 'warning'
  }];
  public statusesOptions = [{
    value: 'Draft',
    label: 'STATUSESDRAFT',
    css: 'primary'
  }, {
    value: 'Online',
    label: 'STATUSESONLINE',
    css: 'success'
  }, {
    value: 'Deactivated',
    label: 'STATUSESDEACTIVATED',
    css: 'warning'
  }, {
    value: 'Offline',
    label: 'STATUSESOFFLINE',
    css: 'danger'
  }, {
    value: 'Deleted',
    label: 'STATUSESDELETED',
    css: 'default'
  }, {
    value: 'Archived',
    label: 'STATUSESARCHIVED',
    css: 'info'
  }];
  public defaultParameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private cache: CacheService,
    private common: CommonService,
    private bannerTypesDataService: BannerTypesDataService,
    private usersDataService: UsersDataService,
    private bannerPositionsDataService: BannerPositionsDataService,

  ) {
    this.getBannerTypes()
    this.getUsers()
    this.getBannerPositions()
  }

  getBannerTypes(offset: number = 0): void {
    const cacheKey: string = 'bannerTypes.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null && offset === 0) {
      this.bannerTypes = cache.results
      return;
    }
    const parameters = Object.assign(this.defaultParameters, {offset: offset + ''});
    this.bannerTypesDataService.getBannerTypes(parameters).subscribe((response: BannerTypesResult|any) => {
      if (response !== null) {
        if (offset === 0) {
          this.bannerTypes = response.results;
        } else {
          this.bannerTypes = this.bannerTypes.concat(response.results);
        }
        if (response.inlineCount > this.bannerTypes.length) {
          offset = offset + parseInt(this.defaultParameters.limit + '', 10);
          this.getBannerTypes(offset);
        }
        response.results = this.bannerTypes;
        this.cache.set(cacheKey, response);
      }
    });
  }

  getUsers(offset: number = 0): void {
    const cacheKey: string = 'users.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null && offset === 0) {
      this.users = cache.results
      return;
    }
    const parameters = Object.assign(this.defaultParameters, {offset: offset + ''});
    this.usersDataService.getUsers(parameters).subscribe((response: UsersResult|any) => {
      if (response !== null) {
        if (offset === 0) {
          this.users = response.results;
        } else {
          this.users = this.users.concat(response.results);
        }
        if (response.inlineCount > this.users.length) {
          offset = offset + parseInt(this.defaultParameters.limit + '', 10);
          this.getUsers(offset);
        }
        response.results = this.users;
        this.cache.set(cacheKey, response);
      }
    });
  }

  getBannerPositions(): void {
    const cacheKey: string = 'bannerPositions.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null) {
      this.bannerPositions = cache.results
      return;
    }
    this.bannerPositionsDataService.getBannerPositions(this.defaultParameters).subscribe((response: BannerPositionsResult|any) => {
      if (response !== null) {
        this.bannerPositions = response.results;
        this.bannerPositionsFiltered = response.results;
        this.cache.set(cacheKey, response);
      }
    });
  }

  filterBannerPositions(searchText: string) {
    if (!searchText) {
      this.bannerPositionsFiltered = Object.assign([], this.bannerPositions);
      return;
    }
    this.bannerPositionsFiltered = []
    this.bannerPositions.map(
      (item: any) => {
        if (item.name.toLowerCase().indexOf(searchText.toLowerCase()) > -1) {
          this.bannerPositionsFiltered.push(item);
        }
      }
    );
  }

  getColumns(): Column[] {
    return [
      { field: 'id', sortField: 'id', title: this.translate.instant('ID'), show: (this.navigation.getParamValue('banners.id.showFiled', true) && true), displayInList: true, type: 'numericValue'},
      { field: 'name', sortField: 'name', title: this.translate.instant('NAME'), show: (this.navigation.getParamValue('banners.name.showFiled', true) && true), displayInList: true, type: 'textValue'},
      { field: 'bannerType', title: this.translate.instant('BANNERTYPE'), type: 'linkValue', filterData: this.bannerTypes, show: (this.navigation.getParamValue('banners.bannerTypeId.showFiled', true) && true), displayInList: true, filterField: 'bannerType.id', displayField: 'name', sortField: 'bannerType.name', url: 'banner-types' },
      { field: 'picture', sortField: 'picture', title: this.translate.instant('PICTURE'), show: (this.navigation.getParamValue('banners.picture.showFiled', true) && true), displayInList: true, type: 'imgValue'},
      { field: 'closable', sortField: 'closable', title: this.translate.instant('CLOSABLE'), show: (this.navigation.getParamValue('banners.closable.showFiled', true) && true), displayInList: true, type: 'booleanValue', filterData: this.navigation.booleanOptions },
      { field: 'countDown', sortField: 'countDown', title: this.translate.instant('COUNTDOWN'), show: (this.navigation.getParamValue('banners.countDown.showFiled', true) && true), displayInList: true, type: 'numericValue'},
      { field: 'webAction', sortField: 'webAction', title: this.translate.instant('WEBACTION'), show: (this.navigation.getParamValue('banners.webAction.showFiled', true) && true), displayInList: true, type: 'textValue'},
      { field: 'androidAction', sortField: 'androidAction', title: this.translate.instant('ANDROIDACTION'), show: (this.navigation.getParamValue('banners.androidAction.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'iphoneAction', sortField: 'iphoneAction', title: this.translate.instant('IPHONEACTION'), show: (this.navigation.getParamValue('banners.iphoneAction.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'gender', sortField: 'gender', title: this.translate.instant('GENDER'), show: (this.navigation.getParamValue('banners.gender.showFiled', false) && true), displayInList: true, type: 'enumValue', filterData: this.gendersOptions},
      { field: 'priority', sortField: 'priority', title: this.translate.instant('PRIORITY'), show: (this.navigation.getParamValue('banners.priority.showFiled', false) && true), displayInList: true, type: 'numericValue'},
      { field: 'minAge', sortField: 'minAge', title: this.translate.instant('MINAGE'), show: (this.navigation.getParamValue('banners.minAge.showFiled', false) && true), displayInList: true, type: 'numericValue'},
      { field: 'maxAge', sortField: 'maxAge', title: this.translate.instant('MAXAGE'), show: (this.navigation.getParamValue('banners.maxAge.showFiled', false) && true), displayInList: true, type: 'numericValue'},
      { field: 'webUrl', sortField: 'webUrl', title: this.translate.instant('WEBURL'), show: (this.navigation.getParamValue('banners.webUrl.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'phoneNumberToCall', sortField: 'phoneNumberToCall', title: this.translate.instant('PHONENUMBERTOCALL'), show: (this.navigation.getParamValue('banners.phoneNumberToCall.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'smsMobileNumber', sortField: 'smsMobileNumber', title: this.translate.instant('SMSMOBILENUMBER'), show: (this.navigation.getParamValue('banners.smsMobileNumber.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'smsBody', sortField: 'smsBody', title: this.translate.instant('SMSBODY'), show: (this.navigation.getParamValue('banners.smsBody.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'emailAdress', sortField: 'emailAdress', title: this.translate.instant('EMAILADRESS'), show: (this.navigation.getParamValue('banners.emailAdress.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'emailSubject', sortField: 'emailSubject', title: this.translate.instant('EMAILSUBJECT'), show: (this.navigation.getParamValue('banners.emailSubject.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'emailBody', sortField: 'emailBody', title: this.translate.instant('EMAILBODY'), show: (this.navigation.getParamValue('banners.emailBody.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'androidAppUrl', sortField: 'androidAppUrl', title: this.translate.instant('ANDROIDAPPURL'), show: (this.navigation.getParamValue('banners.androidAppUrl.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'iphoneAppUrl', sortField: 'iphoneAppUrl', title: this.translate.instant('IPHONEAPPURL'), show: (this.navigation.getParamValue('banners.iphoneAppUrl.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'youtubeUrl', sortField: 'youtubeUrl', title: this.translate.instant('YOUTUBEURL'), show: (this.navigation.getParamValue('banners.youtubeUrl.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'mapLatitude', sortField: 'mapLatitude', title: this.translate.instant('MAPLATITUDE'), show: (this.navigation.getParamValue('banners.mapLatitude.showFiled', false) && true), displayInList: true, type: 'numericValue'},
      { field: 'mapLongitude', sortField: 'mapLongitude', title: this.translate.instant('MAPLONGITUDE'), show: (this.navigation.getParamValue('banners.mapLongitude.showFiled', false) && true), displayInList: true, type: 'numericValue'},
      { field: 'screen', sortField: 'screen', title: this.translate.instant('SCREEN'), show: (this.navigation.getParamValue('banners.screen.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'screenParameters', sortField: 'screenParameters', title: this.translate.instant('SCREENPARAMETERS'), show: (this.navigation.getParamValue('banners.screenParameters.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'template', sortField: 'template', title: this.translate.instant('TEMPLATE'), show: (this.navigation.getParamValue('banners.template.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'adText', sortField: 'adText', title: this.translate.instant('ADTEXT'), show: (this.navigation.getParamValue('banners.adText.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'textColor', sortField: 'textColor', title: this.translate.instant('TEXTCOLOR'), show: (this.navigation.getParamValue('banners.textColor.showFiled', false) && true), displayInList: true, type: 'colorValue'},
      { field: 'backgroundColor', sortField: 'backgroundColor', title: this.translate.instant('BACKGROUNDCOLOR'), show: (this.navigation.getParamValue('banners.backgroundColor.showFiled', false) && true), displayInList: true, type: 'colorValue'},
      { field: 'totalHits', sortField: 'totalHits', title: this.translate.instant('TOTALHITS'), show: (this.navigation.getParamValue('banners.totalHits.showFiled', false) && true), displayInList: true, type: 'numericValue'},
      { field: 'todayHits', sortField: 'todayHits', title: this.translate.instant('TODAYHITS'), show: (this.navigation.getParamValue('banners.todayHits.showFiled', false) && true), displayInList: true, type: 'numericValue'},
      { field: 'totalClicks', sortField: 'totalClicks', title: this.translate.instant('TOTALCLICKS'), show: (this.navigation.getParamValue('banners.totalClicks.showFiled', false) && true), displayInList: true, type: 'numericValue'},
      { field: 'todayClicks', sortField: 'todayClicks', title: this.translate.instant('TODAYCLICKS'), show: (this.navigation.getParamValue('banners.todayClicks.showFiled', false) && true), displayInList: true, type: 'numericValue'},
      { field: 'status', sortField: 'status', title: this.translate.instant('STATUS'), show: (this.navigation.getParamValue('banners.status.showFiled', false) && (this.common.currentUser.roles.join('').indexOf('ROLE_ADMIN_PUBLISHER') > -1)), displayInList: (this.common.currentUser.roles.join('').indexOf('ROLE_ADMIN_PUBLISHER') > -1), type: 'enumValue', filterData: this.statusesOptions},
      { field: 'autoPublishing', sortField: 'autoPublishing', title: this.translate.instant('AUTOPUBLISHING'), show: (this.navigation.getParamValue('banners.autoPublishing.showFiled', false) && true), displayInList: true, type: 'booleanValue', filterData: this.navigation.booleanOptions },
      { field: 'startPublishing', sortField: 'startPublishing', title: this.translate.instant('STARTPUBLISHING'), show: (this.navigation.getParamValue('banners.startPublishing.showFiled', false) && true), displayInList: true, type: 'dateValue', valueFormatter: this.translate.instant('DATETIMEFORMAT')},
      { field: 'endPublishing', sortField: 'endPublishing', title: this.translate.instant('ENDPUBLISHING'), show: (this.navigation.getParamValue('banners.endPublishing.showFiled', false) && true), displayInList: true, type: 'dateValue', valueFormatter: this.translate.instant('DATETIMEFORMAT')},
      { field: 'startPublishingTime', sortField: 'startPublishingTime', title: this.translate.instant('STARTPUBLISHINGTIME'), show: (this.navigation.getParamValue('banners.startPublishingTime.showFiled', false) && true), displayInList: true, type: 'numericValue'},
      { field: 'endPublishingTime', sortField: 'endPublishingTime', title: this.translate.instant('ENDPUBLISHINGTIME'), show: (this.navigation.getParamValue('banners.endPublishingTime.showFiled', false) && true), displayInList: true, type: 'numericValue'},
      { field: 'maxClicksPerDay', sortField: 'maxClicksPerDay', title: this.translate.instant('MAXCLICKSPERDAY'), show: (this.navigation.getParamValue('banners.maxClicksPerDay.showFiled', false) && true), displayInList: true, type: 'numericValue'},
      { field: 'maxTotalClicks', sortField: 'maxTotalClicks', title: this.translate.instant('MAXTOTALCLICKS'), show: (this.navigation.getParamValue('banners.maxTotalClicks.showFiled', false) && true), displayInList: true, type: 'numericValue'},
      { field: 'maxHitsPerDay', sortField: 'maxHitsPerDay', title: this.translate.instant('MAXHITSPERDAY'), show: (this.navigation.getParamValue('banners.maxHitsPerDay.showFiled', false) && true), displayInList: true, type: 'numericValue'},
      { field: 'maxTotalHits', sortField: 'maxTotalHits', title: this.translate.instant('MAXTOTALHITS'), show: (this.navigation.getParamValue('banners.maxTotalHits.showFiled', false) && true), displayInList: true, type: 'numericValue'},
      { field: 'createdAt', sortField: 'createdAt', title: this.translate.instant('CREATEDAT'), show: (this.navigation.getParamValue('banners.createdAt.showFiled', false) && true), displayInList: true, type: 'dateValue', valueFormatter: this.translate.instant('DATETIMEFORMAT')},
      { field: 'creatorUser', title: this.translate.instant('CREATORUSER'), type: 'linkValue', filterData: this.users, show: (this.navigation.getParamValue('banners.creatorUser.showFiled', false) && true), displayInList: true, filterField: 'creatorUser.id', displayField: 'username', sortField: 'creatorUser.username', url: 'users' },
      { field: 'modifiedAt', sortField: 'modifiedAt', title: this.translate.instant('MODIFIEDAT'), show: (this.navigation.getParamValue('banners.modifiedAt.showFiled', false) && true), displayInList: true, type: 'dateValue', valueFormatter: this.translate.instant('DATETIMEFORMAT')},
      { field: 'modifierUser', title: this.translate.instant('MODIFIERUSER'), type: 'linkValue', filterData: this.users, show: (this.navigation.getParamValue('banners.modifierUser.showFiled', false) && true), displayInList: true, filterField: 'modifierUser.id', displayField: 'username', sortField: 'modifierUser.username', url: 'users' },
      { field: 'bannerPositions', title: this.translate.instant('BANNERPOSITIONS'), type: 'linksValue', filterData: this.bannerPositions, show: (this.navigation.getParamValue('banners.bannerPositions.showFiled', false) && true), displayInList: true, display: false, filterField: 'bannerPositions.id', displayField: 'name', sortField: 'bannerPositions[0].name', url: 'banner-positions' },
      { field: 'actions', title: this.translate.instant('ACTIONS'), type: 'static', show: true, displayInList: true, sortField: null},
    ];
  }

  format(fieldName: string, value: any) {
    if (fieldName.toLowerCase().indexOf('time') > -1) {
      return moment(value).format('YYYY-MM-DD HH:mm');
    } else if (fieldName.toLowerCase().indexOf('date') > -1) {
      return moment(value).format('YYYY-MM-DD');
    }
    return value;
  }

}
